import { ReactNode } from 'react';

import { Chip, Link, Rating, Stack, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { CopyableLink } from '../CopyableLink';
import { Icon } from '../Icon';
import { StatusIndicator, StatusIndicatorVariant } from '../StatusIndicator';
import { areaIcons } from '@/constants/areaIcons';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { uiIcons } from '@/constants/uiIcons';
import { formatCurrency } from '@/hooks/useFormatCurrency/formatCurrency';
import { formatNumber } from '@/hooks/useFormatNumber/formatNumber';
import type { HoverCardField, HoverCardFieldType } from '@/lib/resource-config';
import { useLocaleStore } from '@/stores/locale';
import { formatDate } from '@/utils/date/formatDate';
import { pascalToCamelCase } from '@/utils/string';

const FIELD_ICON_MAP: Omit<Record<HoverCardFieldType, string>, 'boolean'> = {
  email: 'alternate_email',
  phone: 'phone',
  date: 'calendar_today',
  currency: 'price_change',
  number: 'tag',
  employee: areaIcons.employees,
  customer: areaIcons.customers,
  project: areaIcons.projects,
  availability: 'event_available',
  text: 'notes',
  department: 'brick',
  legalEntity: 'borg',
  competencies: 'workspace_premium',
  health: uiIcons.healthStatus.health,
};

const getFieldIcon = (type: HoverCardFieldType, value: unknown): string => {
  if (type === 'boolean') {
    return value ? 'check_circle' : 'do_not_disturb_on';
  }
  return FIELD_ICON_MAP[type];
};

interface EntityFieldProps {
  field: HoverCardField;
}

export const EntityField = ({ field }: EntityFieldProps) => {
  const { lang } = useLocaleStore();
  const { t } = useTranslation('ui');

  const { label, value, type, hideIfEmpty, currency, display, entityId } =
    field;

  if (value === null || value === undefined || (value === '' && hideIfEmpty)) {
    return null;
  }

  if (label === undefined) return null;

  let renderedValue: ReactNode;

  switch (type) {
    case 'email':
      renderedValue = (
        <CopyableLink
          href={`mailto:${value}`}
          value={value as string}
          automationId="entity-field-copy-email-button"
        />
      );
      break;
    case 'phone':
      renderedValue = (
        <CopyableLink
          href={`tel:${value}`}
          value={value as string}
          automationId="entity-field-copy-phone-button"
        />
      );
      break;
    case 'boolean':
      if (field.field === 'Active' || field.field === 'IsActive') {
        renderedValue = value ? t('Active') : t('Inactive');
      } else {
        renderedValue = value ? t('Yes') : t('No');
      }
      break;
    case 'date':
      renderedValue = formatDate(value as string, lang);
      break;
    case 'currency':
      renderedValue = formatCurrency(value as number, lang, currency);
      break;
    case 'number':
      renderedValue = formatNumber(value as number, lang);
      break;
    case 'employee':
      renderedValue = entityId ? (
        <Link
          component={RouterLink}
          to={legacyRoutes.employees.details(entityId)}
        >
          {value as string}
        </Link>
      ) : (
        String(value)
      );
      break;
    case 'customer':
      renderedValue = entityId ? (
        <Link
          component={RouterLink}
          to={legacyRoutes.customers.details(entityId)}
        >
          {value as string}
        </Link>
      ) : (
        String(value)
      );
      break;
    case 'project':
      renderedValue = entityId ? (
        <Link
          component={RouterLink}
          to={legacyRoutes.projectManagement.index(entityId)}
        >
          {value as string}
        </Link>
      ) : (
        String(value)
      );
      break;
    case 'health': {
      const variant = pascalToCamelCase(
        value as string,
      ) as StatusIndicatorVariant;
      renderedValue = <StatusIndicator variant={variant} />;
      break;
    }
    case 'competencies': {
      // Competencies are coming in a comma separated format like "Communication (4), Leadership (5), Time Management (3)"
      // We want to split them into individual items and display the name with a rating component for the stars
      // We might restuture data format in the future but this is a quick solution to display competencies with ratings for now.
      const items = String(value)
        .split(/(?<=\))\s*,\s*/)
        .map((s) => {
          // Use regex to extract the name and the number of stars
          // by matching the last parentheses and split them by the comma after that
          // as a competency can have a comma in the name.
          const m = s.match(/^(.+)\s*\((\d+)\)$/);
          return m ? { name: m[1].trim(), stars: Number(m[2]) } : null;
        })
        .filter((item) => item !== null);

      renderedValue = (
        <Stack gap={0.5}>
          {items.map((item) => (
            <Stack
              key={item.name}
              component="dd"
              direction="row"
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <Rating
                name="read-only"
                value={item.stars}
                readOnly
                size="small"
              />
              <Typography variant="label">{item.name}</Typography>
            </Stack>
          ))}
        </Stack>
      );
      break;
    }
    case 'availability':
    case 'department':
    case 'legalEntity':
    case 'text':
    default:
      renderedValue = String(value);
  }

  if (display?.type === 'chip') {
    renderedValue = (
      <Chip
        label={renderedValue}
        size="small"
        color={display.color ?? 'default'}
        monoText
      />
    );
  }

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems={type === 'competencies' ? 'flex-start' : 'center'}
      component="div"
    >
      <Tooltip title={label} disableInteractive>
        <Stack component="span">
          <Icon
            name={getFieldIcon(type, value)}
            size="tiny"
            color="secondary"
          />
        </Stack>
      </Tooltip>
      <Typography component="dt" sx={{ ...visuallyHidden }}>
        {label}:
      </Typography>
      {display?.type === 'chip' || type === 'competencies' ? (
        renderedValue
      ) : (
        <Typography variant="label" component="dd">
          {renderedValue}
        </Typography>
      )}
    </Stack>
  );
};
